/**
 * Seam between the google-location API and a stand-in that answers in the
 * browser, as `features/floor-plan`, `features/feedback` and the website
 * builder have theirs.
 *
 * - true (default): the five calls go to `GoogleLocationAPI`, core-api's
 *   `domain/google-location`.
 * - false: `googleLocationStub` answers every call. It searches a short list
 *   of sample venues and keeps the connected one in this browser, per venue.
 *   Nothing reaches Google. The stories use it through
 *   `GoogleLocationClientContext` whatever this says.
 *
 * The components do not change between the two modes. Until an environment
 * has its Places key and `google_places_enabled` (infra/README.md), search
 * and select answer 503 and the panel says Google is unavailable; reading and
 * disconnecting work regardless, as they never reach Google. Every route
 * needs an owner: anyone else gets a 403, which the panel shows as a failed
 * load.
 *
 * The contract, as called here:
 *
 * - `POST /restaurants/{id}/google-location/autocomplete`
 *   `{ input, sessionToken, restrictToFood }` → `{ suggestions }`
 * - `POST /restaurants/{id}/google-location/select`
 *   `{ placeId, sessionToken }` → `{ location, formattedAddress, address }`,
 *   the address also in parts (street, building, postcode, city). Looks the
 *   place up and stores nothing.
 * - `PUT /restaurants/{id}/google-location` `{ placeId }` → `{ location }`,
 *   keeps a place `select` looked up in the last day; sent by Save
 * - `GET /restaurants/{id}/google-location` → `{ location | null }`
 * - `DELETE /restaurants/{id}/google-location` → 204, sent by Save
 *
 * Under `/restaurants`, like every other route: the core API has no `/api`
 * prefix, that path is its Swagger page. Errors are read by status (400, 404,
 * 409, 429, 503), which map one to one onto the codes the backend puts in
 * `error` (INVALID_INPUT, PLACE_NOT_FOUND, PLACE_NOT_PICKED, RATE_LIMITED,
 * MAPS_UNAVAILABLE); a 429 says how long to wait as `retryAfter` in the body
 * or as a `Retry-After` header.
 * `embedUrl` is empty while the environment has no embed key, and the panel
 * then shows no map.
 *
 * Only the backend talks to the paid Places API. The browser never loads the
 * Maps JavaScript API and holds no key: the one thing it takes from Google is
 * the free Maps Embed iframe, at an `embedUrl` the backend builds.
 *
 * The published website does not call any of this. The builder copies the
 * map and review links into the page's document, as it copies the address.
 */
export const GOOGLE_LOCATION_USE_API = true

/** Backend path, relative to `/restaurants/{restaurantId}`. */
export const GOOGLE_LOCATION_ENDPOINT = '/google-location'

/** Fewer characters than this match too much to be worth a billed request. */
export const MIN_QUERY_LENGTH = 3

/** The backend refuses longer input; the field stops there too. */
export const MAX_QUERY_LENGTH = 100

/** How long typing has to pause before the text is sent. */
export const SEARCH_DEBOUNCE_MS = 300
